import { useRef, useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  IconBook,
  IconCheck,
  IconSearch,
  IconNotebook,
  IconProgressCheck,
} from '@tabler/icons-react';

import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import ListSummaryCardsSkeleton from '@/components/SkeletonLoader/ListSummaryCardsSkeleton.tsx';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import {
  LessonStatusConfig,
  TEACHER_STUDENTS_STATUS_OPTIONS,
} from '@/constants/constant';
import { ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getTeacherClassList,
  getTeacherClassSummary,
} from '@/service/classService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { ISummaryConfig } from '@/types';
import { ITeacherClass } from '@/types/class';
import { formatLastActive } from '@/utils';

const CLASS_SUMMARY_CONFIG: ISummaryConfig[] = [
  {
    key: 'total_class',
    label: 'teacher-classes.total-classes',
    icon: <IconNotebook color="#717784" />,
    value: '0',
  },
  {
    key: 'total_lessons',
    label: 'teacher-classes.total-lessons',
    icon: <IconBook color="#717784" />,
    value: '0',
  },
  {
    key: 'active_students',
    label: 'teacher-classes.active-students',
    icon: <IconCheck color="#717784" />,
    value: '0',
  },
  {
    key: 'avg_completion_rate',
    label: 'teacher-classes.avg-completion-rate',
    icon: <IconProgressCheck color="#717784" />,
    value: '0%',
  },
];

const TeacherClasses = () => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const { isTableLoading, setTableLoading, isScreenLoading, setScreenLoading } =
    useSkeletonStore();

  const [classes, setClasses] = useState<ITeacherClass[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [statsData, setStatsData] =
    useState<ISummaryConfig[]>(CLASS_SUMMARY_CONFIG);

  const isInitialLoad = useRef(true);
  const pageTitle = formatText('teacher-classes.title');

  useEffect(() => {
    setTitle(pageTitle);
    return () => {
      setTitle('');
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) {
      setScreenLoading(loading);
    } else {
      setTableLoading(loading);
    }
  };

  const fetchClasses = async () => {
    setLoadingState(true);
    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (debouncedSearch) query.search = debouncedSearch;
      if (sortColumn) query[`order[${sortColumn}]`] = sortDirection;
      if (statusFilter) query.status = statusFilter;

      const response = await getTeacherClassList(query);
      if (response?.status) {
        setClasses(response.data ?? []);
        setTotal(response.total ?? 0);
      } else {
        setClasses([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingState(false);
      isInitialLoad.current = false;
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await getTeacherClassSummary();
      if (response?.status && response?.data) {
        const apiData = response.data;
        const mappedSummary = CLASS_SUMMARY_CONFIG.map((item) => {
          const value = apiData[item.key];
          return {
            ...item,
            value:
              item.key === 'avg_completion_rate'
                ? `${value ?? 0}%`
                : (value ?? 0),
          };
        });
        setStatsData(mappedSummary);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchClasses();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, sortColumn, sortDirection, statusFilter]);

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const handleRowViewClick = (id: string) => {
    navigate(`/teacher/classes/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: 'teacher-classes.columns.class-name',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: ITeacherClass) => (
          <div className="text-neutral-primary font-medium text-sm py-1.5">
            {record?.name}
          </div>
        ),
      },
      {
        key: 'lesson_created',
        title: 'teacher-classes.columns.lessons-created',
        sorter: true,
        handleSort: () => handleSort('lesson_created'),
        render: (record: ITeacherClass) => (
          <TagCapsule
            value={record?.lesson_created?.toString() || '0'}
            className="text-pink-secondary bg-pink-light-50"
          />
        ),
      },
      {
        key: 'active_students',
        title: 'teacher-classes.columns.active-students',
        sorter: true,
        handleSort: () => handleSort('active_students'),
        render: (record: ITeacherClass) => (
          <TagCapsule
            value={record?.active_students?.toString() || '0'}
            className="text-success-secondary bg-success-light-50"
          />
        ),
      },
      {
        key: 'status',
        title: 'teacher-classes.columns.status',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: ITeacherClass) => {
          const config = record?.status
            ? LessonStatusConfig[record?.status as ELessonStatus]
            : null;
          return (
            <TagCapsule
              value={config?.label ? formatText(config.label) : ''}
              className={config?.className}
            />
          );
        },
      },
      {
        key: 'last_update',
        title: 'teacher-classes.columns.last-update',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        render: (record: ITeacherClass) =>
          formatLastActive(record?.last_update, formatText),
      },
    ],
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [sortColumn, sortDirection, formatText]
  );

  const isFilterApplied = !!statusFilter;
  const showListUI =
    isScreenLoading ||
    classes.length > 0 ||
    search.length > 0 ||
    !!debouncedSearch ||
    isFilterApplied;

  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    classes.length === 0 &&
    (!!debouncedSearch || isFilterApplied);

  if (isScreenLoading) {
    return <ListSummaryCardsSkeleton columns={columns} />;
  }

  if (!showListUI) {
    return (
      <EmptyState
        title={formatText('teacher-classes.empty-title')}
        description={formatText('teacher-classes.empty-description')}
        imageSrc="/assets/svg/class-empty.svg"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="teacher-classes-page">
      <div className="flex flex-wrap justify-start items-center gap-2.5 ps-1">
        <div className="flex items-center gap-4 flex-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText('teacher-classes.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-70"
            inputClassName="py-[13px]"
          />
          <Select
            placeholder={formatText('teacher-classes.columns.status')}
            options={TEACHER_STUDENTS_STATUS_OPTIONS}
            selected={statusFilter}
            onChange={(val: string | string[]) => {
              setStatusFilter(val as string);
              setCurrentPage(1);
            }}
            className="w-37.5! py-3.25"
            isTextFormat
            isClearable
          />
        </div>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="classes-summary-cards"
      >
        {statsData?.map((stat) => (
          <SummaryCard
            key={stat?.key}
            variant="dashboard"
            label={formatText(stat.label)}
            value={stat.value}
            icon={stat?.icon}
            className="bg-white"
          />
        ))}
      </div>

      <CustomTable
        isLoading={isTableLoading}
        columns={columns}
        dataSource={classes}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        fieldName={sortColumn}
        sortDirection={sortDirection}
        isEmptySearch={isEmptySearch}
        onRowClick={handleRowViewClick}
      />
    </div>
  );
};

export default TeacherClasses;
